import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';

import { parseResponseError } from 'common/helpers';
import { getToken } from 'data/token/repositories/tokenRepository';
import { getConnection, getUserSetting } from 'data/fguiPort/datasources/fguiPort.datasource';
import { arrGetSubDashboardComponentV2GroupDetail } from 'models/dashboard/SubDashboardComponentV2';
import useLoader from './useLoader';
import useStore from './useStore';

// Only companies in this side menu group connect FGUI automatically (Setting Port, auto mode)
export const FGUI_AUTO_CONNECT_GROUP = 'F1 ERP GUI CUSTOMERS APPLICATIONS';

// User's Setting Port, cached so menu clicks don't hit the API (SettingPortModal updates it on save)
export const fguiPortSettingQueryKey = (citizenId: string) => ['fgui-port-setting', citizenId];
const SETTING_STALE_TIME = 1000 * 60 * 5;

function useFguiAutoConnect() {
    const { userCompanyStore, subDashboardStore } = useStore();
    const loader = useLoader();
    const queryClient = useQueryClient();

    // The selected side menu item, when it belongs to the auto-connect group
    const getSelectedFguiItem = useCallback(() => {
        const group = subDashboardStore.groupedApplications.find(
            groupApplication => groupApplication.groupName === FGUI_AUTO_CONNECT_GROUP
        );

        return group?.groupItems.find(
            item =>
                item.group_name === subDashboardStore.selectedApplication &&
                item.company_token === subDashboardStore.selectedCompanyToken
        );
    }, [subDashboardStore]);

    /**
     * Connect FGUI for the clicked company using the user's Setting Port.
     * Returns false when the normal (manual) flow should continue.
     */
    const connect = useCallback(
        async (item: arrGetSubDashboardComponentV2GroupDetail): Promise<boolean> => {
            // Only the desktop app can open FGUI; in web mode the setting is just stored
            if (userCompanyStore.mode !== 'gui') {
                return false;
            }

            const sid = item.application_list[0]?.company_id;
            if (!sid) {
                return false;
            }

            try {
                // Manual (the default): nothing to do - no request, no loader
                const setting = await queryClient.fetchQuery({
                    queryKey: fguiPortSettingQueryKey(userCompanyStore.citizenId),
                    queryFn: async () => (await getUserSetting()).data,
                    staleTime: SETTING_STALE_TIME
                });
                if (setting.mode !== 'auto') {
                    return false;
                }

                // Small lookup - no full screen loader so the menu click doesn't flash
                const { data: connection } = await getConnection(sid);
                if (connection.status === 'manual') {
                    return false;
                }

                if (connection.status !== 'ok') {
                    await Swal.fire('แจ้งเตือน', connection.message ?? '', 'warning');

                    return true;
                }

                // Same confirmation as opening the app from its card
                const notifyMessage = userCompanyStore.currentUserCompanyMapping.find(
                    userCompany =>
                        userCompany.sid === sid && userCompany.platform_id === connection.platform_id
                )?.notify_message_when_open;
                if (notifyMessage) {
                    const result = await Swal.fire({
                        title: notifyMessage,
                        icon: 'warning',
                        showDenyButton: true,
                        denyButtonText: 'ไม่ต้องการ',
                        confirmButtonText: 'ต้องการ'
                    });
                    if (!result.isConfirmed) {
                        return true;
                    }
                }

                // Only the token request (right before FGUI opens) shows the loader
                loader.show();
                const { citizenId, email, firstName, lastName } = userCompanyStore;
                const [error, token] = await getToken({
                    company_token: item.company_token,
                    citizen_id: citizenId,
                    email,
                    first_name: firstName,
                    last_name: lastName,
                    mode: localStorage.getItem('mode') || 'web',
                    sid: item.group_name,
                    application: (connection.application_name ?? '').split('\n').join(' ')
                });
                if (error) {
                    throw new Error(error.message);
                }

                (window as any).chrome.webview.postMessage(
                    JSON.stringify({
                        action: 'LOGIN_SUCCESS',
                        token,
                        citizen_id: citizenId,
                        select_server: {
                            system_type: 'GUI',
                            system_id: connection.application_sid,
                            system_name: (connection.application_name ?? '').replace('\n', ' '),
                            host: connection.host,
                            port: connection.port
                        }
                    })
                );

                return true;
            } catch (error) {
                loader.hide();
                const reason = parseResponseError(error);
                await Swal.fire('Error', reason?.message ?? (error as Error).message, 'error');

                return true;
            } finally {
                loader.hide();
            }
        },
        [userCompanyStore, loader, queryClient]
    );

    return { connect, getSelectedFguiItem };
}

export default useFguiAutoConnect;
